import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    TextField,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Paper,
    Alert,
    CircularProgress,
    Snackbar,
    Card,
    CardContent,
    Grid,
    Divider
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format } from 'date-fns';
import DownloadIcon from '@mui/icons-material/Download';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import { useNavigate } from 'react-router-dom';

interface Certificate {
    key: string;
    label: string;
}

const TempCards: React.FC = () => {
    const navigate = useNavigate();
    const [studentName, setStudentName] = useState('');
    const [certDate, setCertDate] = useState<Date | null>(null);
    const [certType, setCertType] = useState('');
    const [certificates, setCertificates] = useState<Certificate[]>([]);
    const [loading, setLoading] = useState(false);
    const [loadingCerts, setLoadingCerts] = useState(true);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    // Load available certificates on component mount
    useEffect(() => {
        fetchCertificates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchCertificates = async () => {
        try {
            const token = localStorage.getItem('access_token') || localStorage.getItem('accessToken');
            if (!token) {
                setError('Please log in to access this feature.');
                navigate('/login');
                return;
            }

            const response = await fetch('/api/temp_cards/certificates', {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.status === 422 || response.status === 401) {
                // Token is invalid or expired
                localStorage.removeItem('access_token');
                setError('Your session has expired. Please log in again.');
                navigate('/login');
                return;
            }

            if (!response.ok) {
                throw new Error('Failed to fetch certificates');
            }

            const data = await response.json();
            setCertificates(data.certificates || []);
        } catch (err) {
            console.error('Failed to fetch certificates:', err);
            setError('Failed to load certificate types');
        } finally {
            setLoadingCerts(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        if (!studentName.trim()) {
            setError('Student name is required');
            return;
        }
        
        if (!certDate) {
            setError('Certification date is required');
            return;
        }
        
        if (!certType) {
            setError('Certificate type is required');
            return;
        }

        setLoading(true);
        setError('');

        try {
            const token = localStorage.getItem('access_token') || localStorage.getItem('accessToken');
            if (!token) {
                setError('Please log in to access this feature.');
                navigate('/login');
                return;
            }

            const response = await fetch('/api/temp_cards/generate', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    student_name: studentName.trim(),
                    cert_date: format(certDate, 'MM/dd/yyyy'),
                    cert_type: certType
                })
            });

            if (response.status === 422 || response.status === 401) {
                // Token is invalid or expired
                localStorage.removeItem('access_token');
                setError('Your session has expired. Please log in again.');
                navigate('/login');
                return;
            }

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to generate certificate');
            }

            // Handle file download
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.style.display = 'none';
            a.href = url;
            
            // Get filename from response headers or create default
            const contentDisposition = response.headers.get('Content-Disposition');
            let filename = 'certificate.pdf';
            if (contentDisposition) {
                const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
                if (filenameMatch) {
                    filename = filenameMatch[1];
                }
            }
            
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);

            setSuccess('Certificate generated and downloaded successfully!');
            
            // Reset form
            setStudentName('');
            setCertDate(null);
            setCertType('');

        } catch (err) {
            console.error('Failed to generate certificate:', err);
            setError(err instanceof Error ? err.message : 'Failed to generate certificate');
        } finally {
            setLoading(false);
        }
    };

    const handleCloseSnackbar = () => {
        setError('');
        setSuccess('');
    };

    if (loadingCerts) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
                <Typography variant="h6" sx={{ ml: 2 }}>Loading certificate types...</Typography>
            </Box>
        );
    }

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Box sx={{ p: 3, maxWidth: 800, mx: 'auto' }}>
                <Paper elevation={2} sx={{ p: 4, borderRadius: 2 }}>
                    {/* Header */}
                    <Box sx={{ textAlign: 'center', mb: 4 }}>
                        <CreditCardIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                        <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                            Generate Certification Cards
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                            Enter the student's information, pick the certification type, and generate a temporary certificate card.
                        </Typography>
                    </Box>

                    <Divider sx={{ mb: 4 }} />

                    {/* Form */}
                    <form onSubmit={handleSubmit}>
                        <Grid container spacing={3}>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Student Name"
                                    variant="outlined"
                                    value={studentName}
                                    onChange={(e) => setStudentName(e.target.value)}
                                    required
                                    placeholder="Enter student's full name"
                                />
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <DatePicker
                                    label="Certification Date"
                                    value={certDate}
                                    onChange={(newValue) => setCertDate(newValue)}
                                    slots={{
                                        textField: TextField
                                    }}
                                    slotProps={{
                                        textField: {
                                            fullWidth: true,
                                            required: true
                                        }
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth required>
                                    <InputLabel>Certificate Type</InputLabel>
                                    <Select
                                        value={certType}
                                        label="Certificate Type"
                                        onChange={(e) => setCertType(e.target.value)}
                                    >
                                        <MenuItem value="">
                                            <em>-- Select Certificate Type --</em>
                                        </MenuItem>
                                        {certificates.map((cert) => (
                                            <MenuItem key={cert.key} value={cert.key}>
                                                {cert.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>

                            <Grid item xs={12}>
                                <Box sx={{ textAlign: 'center', mt: 2 }}>
                                    <Button
                                        type="submit"
                                        variant="contained"
                                        size="large"
                                        disabled={loading}
                                        startIcon={loading ? <CircularProgress size={20} /> : <DownloadIcon />}
                                        sx={{
                                            px: 4,
                                            py: 1.5,
                                            fontSize: '1.1rem',
                                            fontWeight: 'bold',
                                            backgroundColor: 'primary.main',
                                            '&:hover': {
                                                backgroundColor: 'primary.dark',
                                            }
                                        }}
                                    >
                                        {loading ? 'Generating...' : 'Generate PDF Certificate'}
                                    </Button>
                                </Box>
                            </Grid>
                        </Grid>
                    </form>

                    {/* Info Card */}
                    <Card sx={{ mt: 4, backgroundColor: 'info.light', color: 'info.contrastText' }}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                📋 How it works:
                            </Typography>
                            <Typography variant="body2">
                                • Enter the student's name exactly as it should appear on the certificate<br />
                                • Select the certification date<br />
                                • Choose the appropriate certificate type<br />
                                • Click "Generate PDF Certificate" to download the temporary card<br />

                            </Typography>
                        </CardContent>
                    </Card>
                </Paper>

                {/* Snackbars for feedback */}
                <Snackbar
                    open={!!error}
                    autoHideDuration={6000}
                    onClose={handleCloseSnackbar}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                >
                    <Alert onClose={handleCloseSnackbar} severity="error" sx={{ width: '100%' }}>
                        {error}
                    </Alert>
                </Snackbar>

                <Snackbar
                    open={!!success}
                    autoHideDuration={4000}
                    onClose={handleCloseSnackbar}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                >
                    <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%' }}>
                        {success}
                    </Alert>
                </Snackbar>
            </Box>
        </LocalizationProvider>
    );
};

export default TempCards; 